import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, AlertTriangle, Filter, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Inventory = () => {
  const [inventory, setInventory] = useState([]);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [thresholdDialogOpen, setThresholdDialogOpen] = useState(false);
  const [restockDialogOpen, setRestockDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [newStock, setNewStock] = useState(0);
  const [newThreshold, setNewThreshold] = useState(0);
  const [restockQty, setRestockQty] = useState(1);
  const [restockRemarks, setRestockRemarks] = useState('');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [invRes, prodRes, vendRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/inventory`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/products`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/vendors`, { withCredentials: true })
      ]);
      setInventory(invRes.data);
      setProducts(prodRes.data);
      setVendors(vendRes.data);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStock = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(
        `${BACKEND_URL}/api/inventory/${selectedItem.product_id}`,
        { current_stock: newStock },
        { withCredentials: true }
      );
      toast.success('Stock updated');
      fetchAll();
      setDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update stock');
    }
  };

  const handleUpdateThreshold = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(
        `${BACKEND_URL}/api/inventory/${selectedItem.product_id}`,
        { min_stock_threshold: newThreshold },
        { withCredentials: true }
      );
      toast.success('Threshold updated');
      fetchAll();
      setThresholdDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update threshold');
    }
  };

  const openUpdateDialog = (item) => {
    setSelectedItem(item);
    setNewStock(item.current_stock);
    setDialogOpen(true);
  };

  const openThresholdDialog = (item) => {
    setSelectedItem(item);
    setNewThreshold(item.min_stock_threshold || 0);
    setThresholdDialogOpen(true);
  };

  const getProductForInventory = (item) => {
    return products.find(p => p.product_id === item.product_id);
  };

  const getVendorName = (vendorId) => {
    const v = vendors.find(v => v.vendor_id === vendorId);
    return v ? v.vendor_name : 'Unknown';
  };

  const openRestockDialog = (item) => {
    setSelectedItem(item);
    setRestockQty(1);
    setRestockRemarks('');
    setRestockDialogOpen(true);
  };

  const handleRestock = async (e) => {
    e.preventDefault();
    const product = getProductForInventory(selectedItem);
    if (!product) {
      toast.error('Product not found');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/purchase-orders`, {
        vendor_id: product.vendor_id,
        order_date: new Date().toISOString().split('T')[0],
        items: [{ product_id: selectedItem.product_id, quantity: restockQty, remarks: restockRemarks || null }]
      }, { withCredentials: true });
      toast.success(`Purchase order placed to ${getVendorName(product.vendor_id)}`);
      setRestockDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create purchase order');
    }
  };

  const isLowStock = (item) => {
    return item.min_stock_threshold > 0 && item.current_stock <= item.min_stock_threshold;
  };

  const getStockStatus = (item) => {
    if (item.current_stock === 0) {
      return <span className="text-red-700 font-medium">Out of Stock</span>;
    } else if (isLowStock(item)) {
      return <span className="text-amber-700 font-medium">Low Stock</span>;
    }
    return <span className="text-lime-700 font-medium">In Stock</span>;
  };

  const lowStockCount = inventory.filter(isLowStock).length;
  const outOfStockCount = inventory.filter(i => i.current_stock === 0).length;

  let filtered = inventory;
  if (showLowStockOnly) {
    filtered = filtered.filter(i => isLowStock(i) || i.current_stock === 0);
  }
  if (searchFilter) {
    const keywords = searchFilter.toLowerCase().trim().split(/\s+/);
    filtered = filtered.filter(item => {
      const str = `${item.product_name} ${item.product_brand} ${item.product_size} ${item.product_color}`.toLowerCase();
      return keywords.every(kw => str.includes(kw));
    });
  }

  if (loading) {
    return <div className="text-stone-600 text-sm">Loading inventory...</div>;
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Inventory
          </h1>
          <p className="text-xs text-stone-600 mt-1">
            {inventory.length} products
            {lowStockCount > 0 && <span className="text-amber-700 font-medium ml-2">{lowStockCount} low stock</span>}
            {outOfStockCount > 0 && <span className="text-red-700 font-medium ml-2">{outOfStockCount} out of stock</span>}
          </p>
        </div>
        <Button data-testid="refresh-inventory-btn" onClick={fetchAll} variant="outline" size="sm" className="border-stone-200 text-stone-700 hover:bg-stone-50 text-xs h-8">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* Low stock alert */}
      {(lowStockCount > 0 || outOfStockCount > 0) && (
        <div data-testid="low-stock-alert" className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-800">
            <span className="font-semibold">{lowStockCount + outOfStockCount} products need attention.</span> Consider placing purchase orders to restock.
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-stone-200 rounded-md p-3">
        <div className="flex gap-2 items-center">
          <Input
            placeholder="Search product (name, brand, size, color)..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="text-xs h-8 border-stone-200 flex-1"
            data-testid="inventory-search"
          />
          <Button
            data-testid="toggle-low-stock"
            onClick={() => setShowLowStockOnly(!showLowStockOnly)}
            variant={showLowStockOnly ? "default" : "outline"}
            size="sm"
            className={`text-xs h-8 whitespace-nowrap ${showLowStockOnly ? 'bg-amber-700 hover:bg-amber-800 text-white' : 'border-stone-200 text-stone-700'}`}
          >
            <Filter className="w-3 h-3 mr-1" />
            {showLowStockOnly ? 'Showing Low Stock' : 'Low Stock Only'}
          </Button>
        </div>
      </div>

      {/* Stock Update Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Update Stock Level</DialogTitle></DialogHeader>
          {selectedItem && (
            <form onSubmit={handleUpdateStock} className="space-y-4">
              <p className="text-sm text-stone-600">
                Product: <span className="font-medium text-stone-900">{selectedItem.product_name} - {selectedItem.product_brand}</span>
              </p>
              <p className="text-sm text-stone-600">Current Stock: <span className="font-medium text-stone-900 tabular-nums">{selectedItem.current_stock}</span></p>
              <div>
                <Label htmlFor="new_stock">New Stock Level *</Label>
                <Input id="new_stock" data-testid="update-stock-input" type="number" step="0.01" min="0" value={newStock} onChange={(e) => setNewStock(parseFloat(e.target.value))} required className="border-stone-200" />
              </div>
              <Button type="submit" data-testid="submit-stock-btn" className="w-full bg-amber-700 hover:bg-amber-800 text-white">Update Stock</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Threshold Dialog */}
      <Dialog open={thresholdDialogOpen} onOpenChange={setThresholdDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Set Min Stock Threshold</DialogTitle></DialogHeader>
          {selectedItem && (
            <form onSubmit={handleUpdateThreshold} className="space-y-4">
              <p className="text-sm text-stone-600">
                Product: <span className="font-medium text-stone-900">{selectedItem.product_name} - {selectedItem.product_brand}</span>
              </p>
              <p className="text-xs text-stone-500">When stock drops to or below this level, it will be flagged as "Low Stock".</p>
              <div>
                <Label htmlFor="new_threshold">Minimum Stock Threshold</Label>
                <Input id="new_threshold" data-testid="update-threshold-input" type="number" step="0.01" min="0" value={newThreshold} onChange={(e) => setNewThreshold(parseFloat(e.target.value))} required className="border-stone-200" />
              </div>
              <Button type="submit" data-testid="submit-threshold-btn" className="w-full bg-amber-700 hover:bg-amber-800 text-white">Set Threshold</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Restock Dialog */}
      <Dialog open={restockDialogOpen} onOpenChange={setRestockDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Quick Restock</DialogTitle></DialogHeader>
          {selectedItem && (() => {
            const product = getProductForInventory(selectedItem);
            const vendorName = product ? getVendorName(product.vendor_id) : 'Unknown';
            return (
              <form onSubmit={handleRestock} className="space-y-4">
                <div className="bg-stone-50 rounded-md p-3 space-y-1">
                  <p className="text-sm font-medium text-stone-900">{selectedItem.product_name} - {selectedItem.product_brand}</p>
                  <p className="text-xs text-stone-500">{selectedItem.product_size} | {selectedItem.product_color}</p>
                  <div className="flex gap-4 mt-2 text-xs">
                    <span className="text-stone-600">Current Stock: <span className={selectedItem.current_stock === 0 ? 'text-red-600 font-medium' : 'font-medium text-stone-900'}>{selectedItem.current_stock}</span></span>
                    {selectedItem.min_stock_threshold > 0 && <span className="text-stone-600">Min Threshold: <span className="font-medium text-stone-900">{selectedItem.min_stock_threshold}</span></span>}
                  </div>
                  <p className="text-xs text-stone-600 mt-1">Vendor: <span className="font-medium text-stone-900">{vendorName}</span></p>
                </div>
                <div>
                  <Label htmlFor="restock_qty">Quantity to Order *</Label>
                  <Input id="restock_qty" data-testid="restock-qty-input" type="number" step="0.01" min="0.01" value={restockQty} onChange={(e) => setRestockQty(parseFloat(e.target.value) || 0)} required className="border-stone-200" />
                </div>
                <div>
                  <Label htmlFor="restock_remarks">Remarks (optional)</Label>
                  <Input id="restock_remarks" data-testid="restock-remarks-input" value={restockRemarks} onChange={(e) => setRestockRemarks(e.target.value)} className="border-stone-200" placeholder="e.g. Urgent, low stock" />
                </div>
                <Button type="submit" data-testid="submit-restock-btn" className="w-full bg-amber-700 hover:bg-amber-800 text-white">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Place Purchase Order
                </Button>
              </form>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 320px)' }}>
          <table className="w-full text-xs border-collapse">
            <thead className="bg-stone-100 sticky top-0 z-10">
              <tr className="border-b border-stone-300">
                <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-48">PRODUCT</th>
                <th className="border-r border-stone-300 p-2 text-right font-semibold text-stone-700 w-20">STOCK</th>
                <th className="border-r border-stone-300 p-2 text-right font-semibold text-stone-700 w-20">THRESHOLD</th>
                <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-24">STATUS</th>
                <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-24">LAST UPDATED</th>
                <th className="p-2 text-right font-semibold text-stone-700 w-40">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-6 text-center text-stone-500 text-xs">
                    {inventory.length === 0 ? 'No inventory items. Products will appear here automatically.' : 'No items match your filters.'}
                  </td>
                </tr>
              ) : (
                filtered.map((item, index) => (
                  <tr
                    key={item.inventory_id}
                    data-testid={`inventory-row-${item.inventory_id}`}
                    className={`border-b border-stone-200 hover:bg-stone-50 ${
                      isLowStock(item) || item.current_stock === 0 ? 'bg-red-50/50' : index % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'
                    }`}
                  >
                    <td className="border-r border-stone-200 p-2 w-48">
                      <div className="font-medium text-stone-900">{item.product_name}</div>
                      <div className="text-stone-600">{item.product_brand} | {item.product_size} | {item.product_color}</div>
                    </td>
                    <td className="border-r border-stone-200 p-2 text-right font-medium tabular-nums w-20">
                      <span className={item.current_stock === 0 ? 'text-red-700' : isLowStock(item) ? 'text-amber-700' : 'text-stone-900'}>
                        {item.current_stock}
                      </span>
                    </td>
                    <td className="border-r border-stone-200 p-2 text-right tabular-nums text-stone-600 w-20">
                      {item.min_stock_threshold > 0 ? item.min_stock_threshold : '-'}
                    </td>
                    <td className="border-r border-stone-200 p-2 w-24">{getStockStatus(item)}</td>
                    <td className="border-r border-stone-200 p-2 text-stone-600 w-24">{new Date(item.last_updated).toLocaleDateString()}</td>
                    <td className="p-2 text-right w-40">
                      <div className="flex gap-1 justify-end">
                        {(isLowStock(item) || item.current_stock === 0) && (
                          <Button data-testid={`restock-${item.inventory_id}`} onClick={() => openRestockDialog(item)} size="sm" variant="outline" className="border-blue-400 text-blue-700 hover:bg-blue-50 text-xs h-7 px-2">
                            Restock
                          </Button>
                        )}
                        <Button data-testid={`update-stock-${item.inventory_id}`} onClick={() => openUpdateDialog(item)} size="sm" variant="outline" className="border-amber-700 text-amber-700 hover:bg-amber-50 text-xs h-7 px-2">
                          Stock
                        </Button>
                        <Button data-testid={`set-threshold-${item.inventory_id}`} onClick={() => openThresholdDialog(item)} size="sm" variant="outline" className="border-stone-300 text-stone-600 hover:bg-stone-50 text-xs h-7 px-2">
                          Threshold
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-stone-50 border border-stone-200 rounded-md p-2 flex justify-between items-center text-xs">
        <div className="text-stone-600">
          Showing: <span className="font-semibold text-stone-900">{filtered.length}</span> of {inventory.length} products
        </div>
        <div className="text-stone-500">
          Stock auto-deducted on delivery. Set thresholds to get low-stock alerts.
        </div>
      </div>
    </div>
  );
};

export default Inventory;
